"use client";

import type { PaymentMockupCopy } from "@/components/landing/Payments/types";
import { faCheck, faCreditCard, faShieldHalved } from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState } from "react";
import PaymentMethodMark from "@/components/landing/Payments/PaymentMethodMark";
import { Icon } from "@/components/ui/Icon";

const METHOD_IDS = ["card", "cod", "bank"];
const PHASES = ["select", "processing", "success"] as const;
type Phase = (typeof PHASES)[number];
const PHASE_DURATION: Record<Phase, number> = {
	select: 1700,
	processing: 1300,
	success: 2600,
};

interface PaymentCheckoutMockupProps {
	copy: PaymentMockupCopy;
}

/**
 * Looping illustration of one checkout: pick a method, process, then
 *  succeed — so the section reads as "payment already works", not a static
 *  screenshot. Cycles the highlighted method each lap for variety.
 */
function PaymentCheckoutMockup(prop: PaymentCheckoutMockupProps) {
	const { copy } = prop;
	const [phaseIndex, setPhaseIndex] = useState(0);
	const [lap, setLap] = useState(0);
	const phase = PHASES[phaseIndex];
	const selectedMethod = lap % METHOD_IDS.length;

	useEffect(() => {
		const id = setTimeout(() => {
			setPhaseIndex((prev) => {
				const next = (prev + 1) % PHASES.length;
				if (next === 0) setLap((l) => l + 1);
				return next;
			});
		}, PHASE_DURATION[phase]);
		return () => clearTimeout(id);
	}, [phase]);

	const selecting = phase === "select";
	const processing = phase === "processing";
	const success = phase === "success";

	return (
		<div className="flex flex-col gap-6 p-7 sm:p-8" style={{ background: "#fefdfb" }}>
			<div className="flex items-center justify-between gap-3">
				<div>
					<p className="text-[11px] font-medium text-[#9ca3af]">{copy.orderTotalLabel}</p>
					<p className="text-xl font-bold text-[#1c1c1c]">{copy.orderTotalValue}</p>
				</div>
				<span className="flex items-center gap-1.5 rounded-full border border-[#e5e7eb] px-2.5 py-1.5 text-[11px] font-semibold text-[#4b5563]">
					<Icon icon={faShieldHalved} size={12} />
					{copy.secureLabel}
				</span>
			</div>

			<div className="flex items-center gap-2.5">
				{METHOD_IDS.map((id, i) => {
					const active = i === selectedMethod && !success;
					return (
						<span
							key={id}
							className="relative grid h-11 w-16 shrink-0 place-items-center rounded-xl border transition-all duration-500"
							style={
								active
									? {
											borderColor: "var(--brand-1)",
											boxShadow: "0 0 0 3px color-mix(in srgb, var(--brand-1) 12%, transparent)",
											opacity: 1,
										}
									: { borderColor: "#e5e7eb", opacity: success ? 0.35 : 0.55 }
							}
						>
							{active && selecting && (
								<span
									className="absolute inset-0 animate-ping rounded-xl"
									style={{
										boxShadow: "0 0 0 3px color-mix(in srgb, var(--brand-1) 25%, transparent)",
									}}
								/>
							)}
							<PaymentMethodMark id={id} size={16} />
						</span>
					);
				})}
			</div>

			<div className="h-px" style={{ background: "#eef0f2" }} />

			<div
				className="relative flex flex-col items-center py-2 text-center"
				style={{ minHeight: "6.75rem" }}
			>
				<div
					className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition-opacity duration-300"
					style={{ opacity: selecting ? 1 : 0 }}
				>
					<span
						className="grid h-10 w-10 place-items-center rounded-full"
						style={{
							background: "color-mix(in srgb, var(--brand-2) 16%, transparent)",
							color: "var(--brand-1)",
						}}
					>
						<Icon icon={faCreditCard} size={18} />
					</span>
					<p className="text-sm font-medium text-[#6b7280]">{copy.selectLabel}</p>
				</div>

				<div
					className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition-opacity duration-300"
					style={{ opacity: processing ? 1 : 0 }}
				>
					<span
						className="h-10 w-10 animate-spin rounded-full border-3"
						style={{ borderColor: "#e5e7eb", borderTopColor: "var(--brand-1)" }}
					/>
					<p className="text-sm font-medium text-[#6b7280]">{copy.processingLabel}</p>
				</div>

				<div
					className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition-all duration-500"
					style={{
						opacity: success ? 1 : 0,
						transform: success ? "scale(1)" : "scale(0.7)",
					}}
				>
					<span
						className="grid h-16 w-16 place-items-center rounded-full text-white"
						style={{
							background: "linear-gradient(135deg, #34d399, #059669)",
							boxShadow: "0 10px 24px rgba(5, 150, 105, 0.35)",
						}}
					>
						<Icon icon={faCheck} size={28} />
					</span>
					<p className="text-base font-bold text-[#1c1c1c]">{copy.receivedLabel}</p>
				</div>
			</div>

			<div className="flex justify-center">
				<span
					className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-opacity duration-500"
					style={{
						background: "rgba(5, 150, 105, 0.12)",
						color: "#059669",
						opacity: success ? 1 : 0,
					}}
				>
					<span className="relative grid h-1.5 w-1.5 place-items-center">
						<span
							className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
							style={{ background: "#059669" }}
						/>
						<span className="relative h-1.5 w-1.5 rounded-full" style={{ background: "#059669" }} />
					</span>
					{copy.activeLabel}
				</span>
			</div>
		</div>
	);
}

export default PaymentCheckoutMockup;
